import { FC, useState } from "react";
import styled from "styled-components";
import {
  DetailEditModal,
  type EditFieldConfig,
} from "../../components/DetailEditModal";
import {
  Dropdown,
  DropdownInputBorderVariant,
  InputDropdown,
  Item,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import type {
  DeepItem,
  InputItem,
} from "../../../../components/ui-kit/input-drop-down/InputDropdown.interface";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import type {
  CompanyOverviewPatchBody,
  FullCompanyApiType,
} from "../../../../util/types";
import { countries } from "../../../../constants/countries";
import { industries } from "../../../../constants/industries";

const CountryCodeFootnote = styled(Typography).attrs({
  variant: TypographyVariant.BODY4,
})`
  color: #c53030;
  font-weight: 700;
`;

const CountryCodeDropdownShell = styled.div`
  width: 33.333%;
  max-width: 100%;
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const ClearIndustryButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: #027aff;
  text-align: left;
  &:hover {
    text-decoration: underline;
  }
`;

export interface CompanyCoreAddressShape {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  country?: string;
  zipCode?: string;
  region?: string;
}

export interface EditCompanyCoreDetailsModalData {
  _id: string;
  name?: string;
  tradingName?: string;
  countryCode?: string;
  alias?: string;
  /** Same format as signup: "Category / Specific" or legacy category-only string. */
  industry?: string;
  taxNumber?: string;
  publicEmail?: string;
  supportPhone?: string;
  phoneNumber?: string;
  phoneCode?: string;
  phoneCountryCode?: string;
  publicWebsite?: string;
  privacyPolicy?: string;
  termsOfService?: string;
  address?: CompanyCoreAddressShape | null;
  tradingAddress?: CompanyCoreAddressShape | null;
}

export type EditCompanyDetailsSection =
  | "overview"
  | "registered"
  | "trading"
  | "legal"
  | "contact";

const fromAddr = (
  a: CompanyCoreAddressShape | null | undefined,
  defaultCountry: string
): CompanyCoreAddressShape => ({
  addressLine1: a?.addressLine1 ?? "",
  addressLine2: a?.addressLine2 ?? "",
  city: a?.city ?? "",
  country: a?.country ?? defaultCountry,
  zipCode: a?.zipCode ?? "",
  region: a?.region ?? "",
});

const countryItems: Item[] = countries.map((c) => ({
  label: c.countryName,
  value: c.country,
}));

/** Same mapping as ecommerce `CompanyInfo` → `LimitedCompanyForm` (nested industries). */
const MAPPED_INDUSTRIES: DeepItem[] = industries.map((el) => ({
  label: el.category,
  items: el.specifics.map((elem) => ({
    label: elem,
    value: el.category,
  })),
}));

/** Map API string to InputDropdown value (mirrors `DropdownFormField` industries branch). */
function storedIndustryToInputItem(
  stored: string,
  mapped: DeepItem[]
): InputItem {
  const inputString = stored.trim();
  if (!inputString) {
    return { label: "", value: "" };
  }
  const parts = inputString.split(" / ");
  const cat = parts[0]?.trim() ?? "";
  const spec = parts[1]?.trim();
  const found = mapped.find((item) => item.label === cat);
  if (!found) {
    return { label: `${inputString} (current)`, value: inputString };
  }
  if (!spec) {
    const first = found.items[0];
    const v = `${found.label} / ${first.label}`;
    return { label: v, value: v };
  }
  const foundItem = found.items.find((i) => i.label === spec);
  if (!foundItem) {
    return { label: `${inputString} (current)`, value: inputString };
  }
  const v = `${found.label} / ${foundItem.label}`;
  return { label: v, value: v };
}

const validateAddr = (a: CompanyCoreAddressShape): boolean =>
  Boolean(
    a.addressLine1?.trim() &&
      a.city?.trim() &&
      a.country?.trim() &&
      a.zipCode?.trim()
  );

const addrAnySet = (a: CompanyCoreAddressShape) =>
  Boolean(
    a.addressLine1?.trim() ||
      a.addressLine2?.trim() ||
      a.city?.trim() ||
      a.zipCode?.trim() ||
      a.region?.trim()
  );

function toApiAddressPayload(shape: CompanyCoreAddressShape) {
  return {
    addressLine1: shape.addressLine1!.trim(),
    addressLine2: shape.addressLine2?.trim() || undefined,
    city: shape.city!.trim(),
    country: shape.country!.trim(),
    zipCode: shape.zipCode!.trim(),
    region: shape.region?.trim() || undefined,
  };
}

function tradingHadId(data: EditCompanyCoreDetailsModalData): boolean {
  const t = data.tradingAddress;
  return Boolean(
    t && typeof t === "object" && "_id" in t && !!(t as { _id?: string })._id
  );
}

const sectionTitles: Record<EditCompanyDetailsSection, string> = {
  overview: "Edit company overview",
  registered: "Edit registered address",
  trading: "Edit trading address",
  legal: "Edit legal details",
  contact: "Edit contact details",
};

const confirmTitles: Record<EditCompanyDetailsSection, string> = {
  overview: "Confirm company overview changes",
  registered: "Confirm registered address changes",
  trading: "Confirm trading address changes",
  legal: "Confirm legal details changes",
  contact: "Confirm contact details changes",
};

interface EditCompanyCoreDetailsModalProps {
  section: EditCompanyDetailsSection;
  data: EditCompanyCoreDetailsModalData;
  onSuccess: (updated: FullCompanyApiType | undefined) => void | Promise<void>;
}

export const EditCompanyCoreDetailsModal: FC<
  EditCompanyCoreDetailsModalProps
> = ({ section, data, onSuccess }) => {
  const { showNotification } = useNotification();

  const hadTradingId = tradingHadId(data);

  const [initialOverview] = useState(() => ({
    name: (data.name ?? "").trim(),
    tradingName: (data.tradingName ?? "").trim(),
    countryCode: (data.countryCode ?? "GB").trim(),
    alias: (data.alias ?? "").trim(),
    industry: (data.industry ?? "").trim(),
  }));

  const [initialRegistered] = useState(() =>
    fromAddr(data.address ?? undefined, "GB")
  );

  const [initialTrading] = useState(() =>
    fromAddr(
      hadTradingId ? data.tradingAddress ?? undefined : undefined,
      hadTradingId ? "GB" : ""
    )
  );

  const [initialLegal] = useState(() => ({
    taxNumber: (data.taxNumber ?? "").trim(),
  }));

  const [initialContact] = useState(() => ({
    publicEmail: (data.publicEmail ?? "").trim(),
    phoneNumber: (data.phoneNumber ?? "").trim(),
    phoneCode: (data.phoneCode ?? "").trim(),
    phoneCountryCode: (data.phoneCountryCode ?? "").trim(),
    publicWebsite: (data.publicWebsite ?? "").trim(),
    privacyPolicy: (data.privacyPolicy ?? "").trim(),
    termsOfService: (data.termsOfService ?? "").trim(),
  }));

  const handleApiError = (e: unknown) => {
    const err = e as {
      error?: { errors?: { message?: string } };
      message?: string;
    };
    showNotification(
      err?.error?.errors?.message ??
        err?.message ??
        "Could not update company details",
      NotificationTypes.WARNING
    );
    throw e;
  };

  if (section === "legal") {
    const fields: EditFieldConfig[] = [
      {
        name: "taxNumber",
        label: "Tax number",
        type: "text",
        placeholder: "Optional",
      },
    ];
    return (
      <DetailEditModal
        title={sectionTitles.legal}
        confirmTitle={confirmTitles.legal}
        fields={fields}
        initialValues={{ taxNumber: initialLegal.taxNumber }}
        onSubmit={async (values) => {
          const nextTaxNumber = String(values.taxNumber ?? "").trim();
          try {
            const updated = await api.updateCompany(data._id, {
              taxNumber: nextTaxNumber,
            });
            showNotification(
              "Company details updated",
              NotificationTypes.SUCCESS
            );
            await Promise.resolve(onSuccess(updated));
          } catch (e: unknown) {
            handleApiError(e);
          }
        }}
      />
    );
  }

  if (section === "contact") {
    const fields: EditFieldConfig[] = [
      {
        name: "publicEmail",
        label: "Public email",
        type: "text",
        placeholder: "Optional",
      },
      { name: "supportPhone", label: "Support phone", type: "phone" },
      {
        name: "publicWebsite",
        label: "Public website",
        type: "text",
        placeholder: "Optional",
      },
      {
        name: "privacyPolicy",
        label: "Privacy policy URL",
        type: "text",
        placeholder: "Optional",
      },
      {
        name: "termsOfService",
        label: "Terms of service URL",
        type: "text",
        placeholder: "Optional",
      },
    ];
    return (
      <DetailEditModal
        title={sectionTitles.contact}
        confirmTitle={confirmTitles.contact}
        fields={fields}
        initialValues={{
          publicEmail: initialContact.publicEmail,
          supportPhone: {
            number: initialContact.phoneNumber,
            code: initialContact.phoneCode || "+44",
            countryCode: initialContact.phoneCountryCode || "GB",
          },
          publicWebsite: initialContact.publicWebsite,
          privacyPolicy: initialContact.privacyPolicy,
          termsOfService: initialContact.termsOfService,
        }}
        onSubmit={async (values) => {
          const phone = (values.supportPhone as
            | { number: string; code: string; countryCode: string }
            | undefined) ?? { number: "", code: "", countryCode: "" };
          const nextPublicEmail = String(values.publicEmail ?? "").trim();
          const nextPublicWebsite = String(values.publicWebsite ?? "").trim();
          const nextPrivacyPolicy = String(values.privacyPolicy ?? "").trim();
          const nextTermsOfService = String(values.termsOfService ?? "").trim();
          const nextPhoneNumber = (phone.number ?? "").trim();
          const nextPhoneCode = (phone.code ?? "").trim();
          const nextPhoneCountryCode = (phone.countryCode ?? "").trim();

          const body: Record<string, string> = {};
          if (nextPublicEmail !== initialContact.publicEmail)
            body.publicEmail = nextPublicEmail;
          if (nextPhoneNumber !== initialContact.phoneNumber)
            body.phoneNumber = nextPhoneNumber;
          if (nextPhoneCode !== initialContact.phoneCode)
            body.phoneCode = nextPhoneCode;
          if (nextPhoneCountryCode !== initialContact.phoneCountryCode)
            body.phoneCountryCode = nextPhoneCountryCode;
          if (nextPublicWebsite !== initialContact.publicWebsite)
            body.publicWebsite = nextPublicWebsite;
          if (nextPrivacyPolicy !== initialContact.privacyPolicy)
            body.privacyPolicy = nextPrivacyPolicy;
          if (nextTermsOfService !== initialContact.termsOfService)
            body.termsOfService = nextTermsOfService;

          if (Object.keys(body).length === 0) {
            showNotification("No changes to save", NotificationTypes.WARNING);
            return;
          }

          try {
            const updated = await api.updateCompany(data._id, body);
            showNotification(
              "Company details updated",
              NotificationTypes.SUCCESS
            );
            await Promise.resolve(onSuccess(updated));
          } catch (e: unknown) {
            handleApiError(e);
          }
        }}
      />
    );
  }

  if (section === "registered") {
    const fields: EditFieldConfig[] = [
      { name: "address", label: "Registered address", type: "address-group" },
    ];
    return (
      <DetailEditModal
        title={sectionTitles.registered}
        confirmTitle={confirmTitles.registered}
        fields={fields}
        initialValues={{
          address: {
            addressLine1: initialRegistered.addressLine1 ?? "",
            addressLine2: initialRegistered.addressLine2 ?? "",
            city: initialRegistered.city ?? "",
            country: initialRegistered.country ?? "",
            zipCode: initialRegistered.zipCode ?? "",
            region: initialRegistered.region ?? "",
          },
        }}
        validate={(values) => {
          const addr = values.address as CompanyCoreAddressShape | undefined;
          if (!validateAddr(addr ?? {})) {
            return "Registered address: line 1, city, country, and ZIP/postal code are required.";
          }
          return undefined;
        }}
        onSubmit={async (values) => {
          const addr = values.address as CompanyCoreAddressShape;
          try {
            const updated = await api.updateCompanyAddresses(data._id, {
              address: toApiAddressPayload(addr),
            });
            showNotification(
              "Company details updated",
              NotificationTypes.SUCCESS
            );
            await Promise.resolve(onSuccess(updated));
          } catch (e: unknown) {
            handleApiError(e);
          }
        }}
      />
    );
  }

  if (section === "trading") {
    const fields: EditFieldConfig[] = [
      { name: "address", label: "Trading address", type: "address-group" },
    ];
    return (
      <DetailEditModal
        title={sectionTitles.trading}
        confirmTitle={confirmTitles.trading}
        fields={fields}
        initialValues={{
          address: {
            addressLine1: initialTrading.addressLine1 ?? "",
            addressLine2: initialTrading.addressLine2 ?? "",
            city: initialTrading.city ?? "",
            country: initialTrading.country ?? "",
            zipCode: initialTrading.zipCode ?? "",
            region: initialTrading.region ?? "",
          },
        }}
        validate={(values) => {
          const addr = (values.address as CompanyCoreAddressShape) ?? {};
          const shouldRequire = hadTradingId || addrAnySet(addr);
          if (shouldRequire && !validateAddr(addr)) {
            return "Trading address: line 1, city, country, and ZIP/postal code are required.";
          }
          return undefined;
        }}
        onSubmit={async (values) => {
          const addr = (values.address as CompanyCoreAddressShape) ?? {};
          const shouldSend = hadTradingId || addrAnySet(addr);
          if (!shouldSend) {
            showNotification(
              "No trading address to save",
              NotificationTypes.WARNING
            );
            return;
          }
          try {
            const updated = await api.updateCompanyAddresses(data._id, {
              tradingAddress: toApiAddressPayload(addr),
            });
            showNotification(
              "Company details updated",
              NotificationTypes.SUCCESS
            );
            await Promise.resolve(onSuccess(updated));
          } catch (e: unknown) {
            handleApiError(e);
          }
        }}
      />
    );
  }

  // section === "overview"
  const fields: EditFieldConfig[] = [
    { name: "name", label: "Company name", type: "text" },
    { name: "tradingName", label: "Trading name", type: "text" },
    {
      name: "alias",
      label: "Display name",
      type: "text",
      placeholder: "Optional",
    },
    {
      name: "industry",
      label: "Industry",
      type: "custom",
      render: ({ value, setValue }) => {
        const industryValue = String(value ?? "");
        const inputItem = storedIndustryToInputItem(
          industryValue,
          MAPPED_INDUSTRIES
        );
        return (
          <>
            <InputDropdown
              items={MAPPED_INDUSTRIES}
              value={inputItem}
              onChange={(item: InputItem) => setValue(item.value ?? "")}
              fullWidth
              withSearch
              placeholder="Select your industry"
              borderVariant={DropdownInputBorderVariant.DEFAULT}
            />
            {industryValue.trim() !== "" && (
              <ClearIndustryButton type="button" onClick={() => setValue("")}>
                Clear industry
              </ClearIndustryButton>
            )}
          </>
        );
      },
    },
    {
      name: "countryCode",
      label: "Country code",
      type: "custom",
      render: ({ value, setValue }) => (
        <>
          <CountryCodeFootnote>
            If this is not United Kingdom (GB), Fena Open Banking payments will
            not work.
          </CountryCodeFootnote>
          <CountryCodeDropdownShell>
            <Dropdown
              fullWidth
              items={countryItems}
              defaultItem={
                countryItems.find((i) => i.value === value) ?? countryItems[0]
              }
              onChange={(item: Item) => setValue(String(item.value))}
            />
          </CountryCodeDropdownShell>
        </>
      ),
    },
  ];

  return (
    <DetailEditModal
      title={sectionTitles.overview}
      confirmTitle={confirmTitles.overview}
      fields={fields}
      initialValues={{
        name: initialOverview.name,
        tradingName: initialOverview.tradingName,
        alias: initialOverview.alias,
        industry: initialOverview.industry,
        countryCode: initialOverview.countryCode,
      }}
      onSubmit={async (values) => {
        const body: CompanyOverviewPatchBody = {};
        const nextName = String(values.name ?? "").trim();
        const nextTradingName = String(values.tradingName ?? "").trim();
        const nextAlias = String(values.alias ?? "").trim();
        const nextIndustry = String(values.industry ?? "").trim();
        const nextCountryCode = String(values.countryCode ?? "").trim();

        if (nextName !== initialOverview.name) body.name = nextName;
        if (nextTradingName !== initialOverview.tradingName)
          body.tradingName = nextTradingName;
        if (nextCountryCode !== initialOverview.countryCode)
          body.countryCode = nextCountryCode;
        if (nextAlias !== initialOverview.alias) body.alias = nextAlias;
        if (nextIndustry !== initialOverview.industry)
          body.industry = nextIndustry;

        if (Object.keys(body).length === 0) {
          showNotification("No changes to save", NotificationTypes.WARNING);
          return;
        }

        try {
          const updated = await api.updateCompanyOverview(data._id, body);
          showNotification(
            "Company details updated",
            NotificationTypes.SUCCESS
          );
          await Promise.resolve(onSuccess(updated));
        } catch (e: unknown) {
          handleApiError(e);
        }
      }}
    />
  );
};
